import { useMemo } from "react";
import { blogPostsQuery, siteSettingsQuery } from "@/lib/sanity";
import { useSanityQuery } from "@/hooks/use-sanity-query";
import type { BlogPost, SiteSettings } from "@/types/content";

const fallbackPosts: BlogPost[] = [
  {
    id: "fallback-1",
    title: "Designing Zero-Trust Networks That Scale",
    publishedAt: "2024-09-18",
    excerpt:
      "A practical blueprint for segmenting environments, reducing blast radius, and keeping operational overhead low.",
    tags: ["Zero Trust", "Architecture"],
  },
  {
    id: "fallback-2",
    title: "Threat Hunting Playbooks for Lean Teams",
    publishedAt: "2024-08-02",
    excerpt:
      "How to build repeatable hunt workflows with the data sources you already have, plus quick wins that prove value.",
    tags: ["Threat Hunting", "Operations"],
  },
  {
    id: "fallback-3",
    title: "Automation Wins: Hardening Cloud Accounts in Minutes",
    publishedAt: "2024-07-15",
    excerpt:
      "The scripts, guardrails, and checklists I use to harden cloud accounts without slowing down shipping.",
    tags: ["Automation", "Cloud"],
  },
];

export default function BlogSection() {
  const { data: settings } = useSanityQuery<SiteSettings>(["sanity", "siteSettings"], siteSettingsQuery);
  const { data: postsData } = useSanityQuery<BlogPost[]>(["sanity", "blogPosts"], blogPostsQuery);

  const posts = useMemo(() => {
    if (postsData && postsData.length > 0) {
      return postsData;
    }
    return fallbackPosts;
  }, [postsData]);

  const heading = settings?.blogHeading || "> ./blog";
  const intro =
    settings?.blogIntro || "Deep dives, field notes, and practical guides on securing modern infrastructure.";

  if (settings?.blogSectionEnabled === false) {
    return null;
  }

  const formatDate = (value?: string) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <section id="blog" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-mono text-pink-400">
            {heading}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {intro}
          </p>
        </div>

        {posts.length > 0 ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => {
              const formattedDate = formatDate(post.publishedAt);
              return (
                <article
                  key={post.id}
                  className="glass-card p-6 rounded-2xl flex flex-col justify-between hover:scale-[1.01] transition-transform"
                >
                  <div>
                    {formattedDate && (
                      <p className="text-sm text-gray-400 font-mono mb-3">{formattedDate}</p>
                    )}
                    <h3 className="text-2xl font-bold mb-3 text-white font-mono">{post.title}</h3>
                    {post.excerpt && <p className="text-gray-300 mb-6">{post.excerpt}</p>}
                  </div>
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs font-mono px-3 py-1 rounded-full bg-pink-400/10 text-pink-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-gray-300 font-mono">No blog posts published yet.</div>
        )}

        {settings?.blogCtaLabel && settings?.blogCtaUrl && (
          <div className="mt-12 flex justify-center">
            <a
              className="inline-flex items-center rounded-lg border border-pink-400 px-6 py-3 font-mono text-pink-300 hover:bg-pink-400/10 transition-colors"
              href={settings.blogCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {settings.blogCtaLabel}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
